// The NY calendar windows the events reads are keyed on.
//
// This lived inside `events.tsx` as a local function, and `venues.tsx` had its
// own hand-rolled copy of the `this_week` arm. That was survivable while only
// components computed it — and became a real defect the moment route loaders
// existed, because `eventsList` declares no `keyOf`, so **the whole query object
// is the cache key**. `eventsList({})` and `eventsList({date_from, date_to})`
// are different slots.
//
// Both loaders warmed `{}`. Nothing in the app reads `{}`: `/backend/events`
// opens on `next7` and `/backend/venues` on `this_week`. So the route blocked
// first paint on an unfiltered `GET /events` — the 1000 OLDEST performances —
// discarded it, and then mounted cold anyway, which is strictly worse than
// having no loader at all. The fix is not to correct two literals; it is to
// make the loader and the screen unable to disagree, by giving them one
// function to call.
//
// `nowMs` is injectable so the derivation is testable without stubbing the
// clock. It is NOT there to let a caller pass a different clock in production:
// the loader and the component must resolve the same NY day or they open two
// slots, and the only window where they cannot is a page load spanning NY
// midnight — where a single extra fetch is the correct outcome anyway.

import { fmtDateInput, nyStartOfDayMs } from '@/lib/datetime';
import type { EventsQuery } from '@/api/resources';

export type EventRange = 'today' | 'next7' | 'past_week' | 'this_week';

/**
 * Every window is bounded. There is no `all`: `/events` refuses a read that
 * matches more than 1000 performances, and an unfiltered read of 39k would
 * only ever be refused. A `?window=all` URL falls back to the default window.
 */
export const EVENT_RANGES = ['today', 'next7', 'past_week', 'this_week'] as const;

/** What `/backend/events` opens on, and therefore what its loader must warm. */
export const EVENTS_SCREEN_DEFAULT_RANGE: EventRange = 'next7';

/** What the venues screen's performance panel reads. */
export const VENUES_SCREEN_EVENT_RANGE: EventRange = 'this_week';

/**
 * Map a relative window to the API's `/events` day-string params.
 *
 * The server reads `date_from`/`date_to` as day strings, so these are NY
 * calendar days built off NY "today" — the window matches the operator's NY day
 * regardless of the viewer's browser timezone. Scoping the fetch server-side is
 * also what keeps each response well under the 1000-row cap.
 */
export function eventsRangeQuery(range: EventRange, nowMs: number = Date.now()): EventsQuery {
  // The range can come straight from a URL a person typed. The
  // fallback is the DEFAULT WINDOW, never `{}` — an unrecognised value must not
  // silently produce an unfiltered read of the 1000 oldest performances, which
  // is the precise shape of the bug this module was extracted to kill.
  const safe: EventRange = EVENT_RANGES.includes(range) ? range : EVENTS_SCREEN_DEFAULT_RANGE;
  const todayMs = nyStartOfDayMs(nowMs);
  // Add whole NY days: the +12h cushion lands mid-day of the target before we
  // snap back to that NY day's midnight, so DST-length days can't slip the date.
  const addDays = (ms: number, n: number) => nyStartOfDayMs(ms + n * 86400000 + 12 * 3600000);

  switch (safe) {
    case 'today':
      return { date_from: fmtDateInput(todayMs), date_to: fmtDateInput(todayMs) };
    case 'next7':
      return { date_from: fmtDateInput(todayMs), date_to: fmtDateInput(addDays(todayMs, 6)) };
    case 'past_week':
      return { date_from: fmtDateInput(addDays(todayMs, -7)), date_to: fmtDateInput(addDays(todayMs, -1)) };
    case 'this_week': {
      // NY weekday — NY midnight shares its UTC date, so Sun(0)→7 starts the
      // week on Monday.
      const dow = new Date(todayMs).getUTCDay() || 7;
      const monday = addDays(todayMs, -(dow - 1));
      return { date_from: fmtDateInput(monday), date_to: fmtDateInput(addDays(monday, 6)) };
    }
  }
}
